/**
 * The MECH sheet's **EDIT mode** (`decisions/0206` point 4): the controls {@link MechBuildSheet}
 * mounts in place when its host passes a {@link MechSheetEdit}: the name input, CHANGE FRAME and
 * REPLACE ART in the header, SWAP / ✕ / ＋ MOD on weapon rows, ＋ EQUIP on empty slots, RETROFIT on a
 * mount label, ✕ and ＋ INSTALL SYSTEM in SYSTEMS, and the red `EDITING · AUTO-SAVED · UNDO · REDO ·
 * DONE` bar. Pickers are `CompendiumExplorer`s.
 *
 * Every control dispatches a roster command (`rename_mech`,
 * `select_frame`, `set_mech_image`, `equip_weapon`, `unequip_weapon`, `attach_mod`, `detach_mod`,
 * `retrofit_mount`, `equip_system`, `unequip_system`); SWAP is an atomic `batch` of the slot's
 * `unequip_weapon` and `equip_weapon`, one undo step, because `equip_weapon` refuses an occupied
 * slot. The pickers mirror the reducer's gates through the same helpers (`equipEntry`,
 * `computeSp*`, `weaponFitsSlot`, `modFitsWeapon`, `superheavyBraceState`); the reducer stays the
 * wall, and a rejection shows in the sheet's error line.
 *
 * **The Loadout flags.** On the pilot's active mech, `buildSignals`' Loadout signals paint the
 * WEAPONS and SYSTEMS heads, as the PILOT sheet's sections are painted, and FRAME, WEAPONS and
 * SYSTEMS carry the FORGE anchors a jump lands on (`forgeGuide.ts`).
 */
import { faPlus, faRightLeft, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState, type ReactNode } from "react";
import {
  composedMountProvenance,
  composedMounts,
  computeSpMax,
  computeSpUsed,
  MOUNT_RETROFITTING_BONUS_ID,
  modFitsWeapon,
  resolveActiveMechId,
  slotSizesForMount,
  superheavyBraceState,
  weaponFitsSlot,
  type MechData,
  type PilotData,
  type RosterCommand,
} from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";
import type { ComputedPilot } from "@lancer-vtt/rules";
import { CompendiumExplorer, sortByName } from "../../lib/editor";
import { buildSignals, type BuildSignal, type LoadoutPart } from "../../lib/editor/buildAdvisor";
import { Icon } from "../../lib/Icon";
import { uploadMechImage } from "../../lib/api";
import { useDraftText } from "../../lib/useDraftText";
import type { LoadoutGroup, LoadoutRow } from "../../lib/encounter/unitLoadout";
import { CompendiumSource, type Ref } from "../compendium/compendiumData";
import { CompendiumRecordReader } from "../compendium/CompendiumDetail";
import { braceWeaponLabel, equipEntry } from "./mechEquipEntries";
import { LOADOUT_ANCHOR } from "./forgeGuide";
import { ENGINEER_TALENT_ID, MechPrototypeEdit, PrototypeRerollBand, type RollDice } from "./MechPrototypeEdit";
import { EditBar, EditToggle, ImageUpload, imageRejection, sectionFlag, type SheetEdit } from "./sheetEdit";

/**
 * The MECH sheet's editing capability: undo over `mechScope`, and the COMMS roll the PROTOTYPE
 * WEAPON block's Limited roll posts through (absent: the block offers the stepper only).
 */
export type MechSheetEdit = SheetEdit & { rollDice?: RollDice };

type Explorer =
  | { kind: "frame" }
  | { kind: "weapon"; mountIdx: number; slotIdx: number; slotSize: string; swap: boolean }
  | { kind: "mod"; mountIdx: number; slotIdx: number; weaponType: string | undefined }
  | { kind: "system" };

/** What EDIT mode adds to the sheet, slot by slot; every slot is `undefined` while EDIT is off. */
export type MechSheetEditSlots = {
  /** The EDIT / EDITING switch for the header's actions. */
  toggle?: ReactNode;
  nameEditor?: ReactNode;
  /** CHANGE FRAME (or SELECT FRAME) and REPLACE ART, after the kind line. */
  headerControls?: ReactNode;
  toolbar?: ReactNode;
  error?: string | null;
  /** The loadout groups with WEAPONS' and SYSTEMS' anchors and Loadout flags. */
  groups?: (groups: LoadoutGroup[]) => LoadoutGroup[];
  rowActions?: (row: LoadoutRow, group: LoadoutGroup) => ReactNode;
  sectionActions?: (section: string, row: LoadoutRow) => ReactNode;
  groupFoot?: (group: LoadoutGroup) => ReactNode;
  /** The PROTOTYPE WEAPON block (`MechPrototypeEdit`), while the pilot holds Engineer. */
  prototype?: ReactNode;
  /** The prototype's re-roll prompt (`PrototypeRerollBand`), in and out of EDIT; it renders nothing until a roll is owed. */
  prototypeBand?: ReactNode;
  /** The open picker. */
  overlay?: ReactNode;
};

/** `"0:1"` / `"0:1:mod"` → the composed mount and slot a weapon or mod row stands for. */
function slotOf(key: string): { mountIdx: number; slotIdx: number } | undefined {
  const m = /^(\d+):(\d+)(?::mod)?$/.exec(key);
  return m ? { mountIdx: Number(m[1]), slotIdx: Number(m[2]) } : undefined;
}

export function useMechSheetEdit({
  edit,
  pilot,
  mech,
  computed,
  compendium,
  onOpenInCompendium,
}: {
  edit: MechSheetEdit | null;
  pilot: PilotData;
  mech: MechData;
  computed: ComputedPilot | undefined;
  compendium: Compendium;
  onOpenInCompendium: (ref: Ref) => void;
}): MechSheetEditSlots {
  const [explorer, setExplorer] = useState<Explorer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const source = useMemo(() => new CompendiumSource(compendium), [compendium]);

  const dispatch = (cmd: RosterCommand) => {
    if (!edit) return;
    const res = edit.dispatch(cmd);
    setError(res.ok ? null : res.error);
  };
  const ids = { actorId: edit?.actorId ?? "", pilotId: pilot.id, mechId: mech.id };
  // Draft-buffered like the desktop editor's: one `rename_mech` on debounce or blur.
  const nameDraft = useDraftText(mech.name, (name) => dispatch({ type: "rename_mech", ...ids, name }));

  if (!edit) return {};
  const toggle = <EditToggle edit={edit} />;
  const prototypeBand = (
    <PrototypeRerollBand pilot={pilot} mech={mech} actorId={edit.actorId} dispatch={dispatch} rollDice={edit.rollDice} onReview={edit.editing ? undefined : () => edit.onEditing(true)} />
  );
  if (!edit.editing) return { toggle, prototypeBand };

  const loadout = mech.loadouts[mech.activeLoadoutIndex];
  const spRemaining = computeSpMax(mech, pilot, compendium) - computeSpUsed(mech, pilot, compendium);
  const mounts = loadout ? composedMounts(loadout, pilot, compendium) : [];
  const provenance = loadout ? composedMountProvenance(loadout, pilot, compendium) : [];
  // `retrofit_mount`'s gate: the pilot holds the bonus and no mount is retrofitted yet.
  const canRetrofit = pilot.coreBonuses.includes(MOUNT_RETROFITTING_BONUS_ID) && !loadout?.retrofit;
  // With no bracing slack, every empty mount is a Superheavy's required brace: RESERVED, not ＋ EQUIP.
  const brace = superheavyBraceState(mech, pilot, compendium);
  const braceReserved = !!brace && brace.freeMounts <= brace.required;
  const loadoutIds = loadout ? { ...ids, loadoutId: loadout.id } : undefined;

  async function onImage(file: File) {
    const rejected = imageRejection(file);
    if (rejected) return setError(rejected);
    setImgBusy(true);
    const uploaded = await uploadMechImage(mech.id, file);
    if (uploaded.ok) dispatch({ type: "set_mech_image", ...ids, image: uploaded.value });
    else setError(uploaded.error);
    setImgBusy(false);
  }

  const nameEditor = (
    <input
      className="unit-sheet-name-input"
      value={nameDraft.value}
      placeholder="UNNAMED MECH"
      aria-label="Mech name"
      onFocus={nameDraft.onFocus}
      onChange={(e) => nameDraft.onChange(e.target.value)}
      onBlur={nameDraft.onBlur}
    />
  );

  // The advisor reads the active mech, so only the active mech's sheet carries its Loadout signals.
  const loadoutSignals = new Map<LoadoutPart, BuildSignal[]>();
  if (resolveActiveMechId(pilot) === mech.id) {
    for (const s of buildSignals({ pilot, computed, compendium })) {
      if (s.section !== "Loadout") continue;
      const part = s.part ?? "weapons";
      loadoutSignals.set(part, [...(loadoutSignals.get(part) ?? []), s]);
    }
  }
  const frameFlag = sectionFlag(loadoutSignals.get("frame"));

  const headerControls = (
    <>
      <span className={`unit-sheet-frame-ctl${frameFlag ? ` is-flag-${frameFlag.severity}` : ""}`} title={frameFlag?.title} data-build-anchor={LOADOUT_ANCHOR.frame}>
        <button type="button" className="unit-sheet-mini" data-forge-focus="" onClick={() => setExplorer({ kind: "frame" })}>
          {mech.frameId ? "CHANGE FRAME" : <><Icon icon={faPlus} className="icon-lead" />SELECT FRAME</>}
        </button>
      </span>
      <ImageUpload label="Upload mech portrait image" busy={imgBusy} hasImage={!!mech.image} noun="ART" onFile={onImage} />
    </>
  );

  const groups = (groups: LoadoutGroup[]): LoadoutGroup[] =>
    groups.map((g) => {
      if (g.id !== "weapons" && g.id !== "systems") return g;
      const flag = sectionFlag(loadoutSignals.get(g.id));
      return { ...g, anchor: LOADOUT_ANCHOR[g.id], ...(flag ? { flag } : {}) };
    });

  const toolbar = <EditBar edit={edit} />;

  const rowActions = (row: LoadoutRow, group: LoadoutGroup): ReactNode => {
    if (!loadoutIds) return undefined;
    if (group.id === "systems") {
      // `talentsystem:N` rows are talent grants: not installed, so not removable.
      if (!row.key.startsWith("system:") || !row.ref) return undefined;
      const systemId = row.ref.id;
      return (
        <button type="button" className="unit-sheet-mini" aria-label={`uninstall ${row.name}`} onClick={() => dispatch({ type: "unequip_system", ...loadoutIds, systemId })}>
          <Icon icon={faXmark} />
        </button>
      );
    }
    if (group.id !== "weapons") return undefined;
    const at = slotOf(row.key);
    if (!at) return undefined; // integrated weapons ride no mount
    const mount = mounts[at.mountIdx];
    const slot = mount?.slots[at.slotIdx];
    if (!mount || !slot) return undefined;
    const slotSize = slotSizesForMount(mount.type)?.[at.slotIdx] ?? mount.type;
    if (row.variant === "sub") {
      return (
        <button type="button" className="unit-sheet-mini" aria-label="detach mod" onClick={() => dispatch({ type: "detach_mod", ...loadoutIds, ...at })}>
          <Icon icon={faXmark} />
        </button>
      );
    }
    if (row.variant === "locked") {
      return (
        <span
          className="unit-sheet-mini is-reserved"
          title="Flexible mounts take one Main weapon OR up to two Auxiliary weapons, not both (core p.32). Unequip the Main weapon to free this slot for a second Auxiliary."
        >
          LOCKED · MAIN EQUIPPED
        </span>
      );
    }
    if (row.variant === "empty") {
      if (braceReserved && mount.slots.every((s) => !s.weaponId)) {
        return (
          <span
            className="unit-sheet-mini is-reserved"
            title={`This mount must stay empty to brace ${brace ? braceWeaponLabel(brace, compendium) : "a Superheavy weapon"} (Superheavy weapons need a Heavy mount plus one other mount free). Unequip the Superheavy to free it.`}
          >
            RESERVED · BRACING
          </span>
        );
      }
      return (
        <button type="button" className="unit-sheet-mini" data-forge-focus="" onClick={() => setExplorer({ kind: "weapon", ...at, slotSize, swap: false })}>
          <Icon icon={faPlus} className="icon-lead" />
          EQUIP · {String(slotSize).toUpperCase()}
        </button>
      );
    }
    const weaponType = slot.weaponId ? compendium.getById("weapon", slot.weaponId)?.type : undefined;
    // Offer ＋ MOD only when the picker would list something; never on a no-mods bonus mount (`attach_mod`'s gate 0).
    const modOptions =
      !slot.modId &&
      !provenance[at.mountIdx]?.noMods &&
      compendium.list("mod").some((m) => modFitsWeapon(weaponType, m.allowed_types) && (m.sp ?? 0) <= spRemaining);
    return (
      <>
        <button type="button" className="unit-sheet-mini" aria-label={`swap ${row.name}`} onClick={() => setExplorer({ kind: "weapon", ...at, slotSize, swap: true })}>
          SWAP
        </button>
        {modOptions && (
          <button type="button" className="unit-sheet-mini" onClick={() => setExplorer({ kind: "mod", ...at, weaponType })}>
            <Icon icon={faPlus} className="icon-lead" />
            MOD
          </button>
        )}
        <button type="button" className="unit-sheet-mini" aria-label={`unequip ${row.name}`} onClick={() => dispatch({ type: "unequip_weapon", ...loadoutIds, ...at })}>
          <Icon icon={faXmark} />
        </button>
      </>
    );
  };

  const sectionActions = (_section: string, row: LoadoutRow): ReactNode => {
    const at = slotOf(row.key);
    if (!canRetrofit || !loadoutIds || !loadout || !at || at.mountIdx >= loadout.mounts.length) return undefined;
    return (
      <button
        type="button"
        className="unit-sheet-mini"
        title="Replace this mount with a Main/Aux mount (Mount Retrofitting)"
        onClick={() => dispatch({ type: "retrofit_mount", ...loadoutIds, mountIdx: at.mountIdx })}
      >
        <Icon icon={faRightLeft} className="icon-lead" />
        RETROFIT
      </button>
    );
  };

  const groupFoot = (group: LoadoutGroup): ReactNode =>
    group.id === "systems" && loadoutIds ? (
      <div className="unit-sheet-lgroup-foot">
        <button type="button" className="unit-sheet-mini" aria-label="Add system" data-forge-focus="" onClick={() => setExplorer({ kind: "system" })}>
          <Icon icon={faPlus} className="icon-lead" />
          INSTALL SYSTEM · {spRemaining} SP FREE
        </button>
      </div>
    ) : undefined;

  const reader = (type: Ref["type"]) => (id: string) => <CompendiumRecordReader source={source} item={{ type, id }} onNavigate={onOpenInCompendium} />;
  const close = () => setExplorer(null);
  const pick = (cmd: RosterCommand) => {
    dispatch(cmd);
    close();
  };
  let overlay: ReactNode;
  if (explorer?.kind === "frame") {
    overlay = (
      <CompendiumExplorer
        title="SELECT FRAME"
        entries={sortByName(compendium.list("frame")).map((f) => equipEntry({ pilot, mech, compendium, item: f, type: "frame", extraMeta: f.source }))}
        renderDetail={reader("frame")}
        onSelect={(frameId) => pick({ type: "select_frame", ...ids, frameId })}
        onClose={close}
      />
    );
  } else if (explorer?.kind === "weapon" && loadoutIds) {
    const { mountIdx, slotIdx, slotSize, swap } = explorer;
    overlay = (
      <CompendiumExplorer
        title={`${swap ? "SWAP" : "EQUIP"} · ${String(slotSize).toUpperCase()}`}
        entries={sortByName(compendium.list("weapon").filter((w) => weaponFitsSlot(slotSize, w.mount))).map((w) =>
          equipEntry({
            pilot,
            mech,
            compendium,
            item: w,
            type: "weapon",
            candidate: { surface: "weapon", mountIdx, slotIdx, id: w.id },
            extraMeta: [w.mount, typeof w.sp === "number" ? `SP ${w.sp}` : undefined].filter(Boolean).join(" · "),
          }),
        )}
        renderDetail={reader("weapon")}
        onSelect={(weaponId) => {
          const equip: RosterCommand = { type: "equip_weapon", ...loadoutIds, mountIdx, slotIdx, weaponId };
          pick(swap ? { type: "batch", commands: [{ type: "unequip_weapon", ...loadoutIds, mountIdx, slotIdx }, equip] } : equip);
        }}
        onClose={close}
      />
    );
  } else if (explorer?.kind === "mod" && loadoutIds) {
    const { mountIdx, slotIdx, weaponType } = explorer;
    overlay = (
      <CompendiumExplorer
        title="ATTACH MOD"
        accent
        entries={sortByName(compendium.list("mod").filter((m) => modFitsWeapon(weaponType, m.allowed_types) && (m.sp ?? 0) <= spRemaining)).map((m) =>
          equipEntry({ pilot, mech, compendium, item: m, type: "mod", candidate: { surface: "mod", mountIdx, slotIdx, id: m.id }, extraMeta: `SP ${m.sp ?? 0}` }),
        )}
        renderDetail={reader("mod")}
        onSelect={(modId) => pick({ type: "attach_mod", ...loadoutIds, mountIdx, slotIdx, modId })}
        onClose={close}
      />
    );
  } else if (explorer?.kind === "system" && loadoutIds) {
    overlay = (
      <CompendiumExplorer
        title="INSTALL SYSTEM"
        entries={sortByName(compendium.list("system").filter((s) => (s.sp ?? 0) <= spRemaining)).map((s) =>
          equipEntry({ pilot, mech, compendium, item: s, type: "system", candidate: { surface: "system", id: s.id }, extraMeta: `SP ${s.sp ?? 0}` }),
        )}
        renderDetail={reader("system")}
        onSelect={(systemId) => pick({ type: "equip_system", ...loadoutIds, systemId })}
        onClose={close}
      />
    );
  }

  const prototype =
    (pilot.talents[ENGINEER_TALENT_ID] ?? 0) > 0 || mech.prototype !== undefined ? (
      <MechPrototypeEdit pilot={pilot} mech={mech} actorId={edit.actorId} dispatch={dispatch} rollDice={edit.rollDice} />
    ) : undefined;

  return { toggle, nameEditor, headerControls, toolbar, error, groups, rowActions, sectionActions, groupFoot, prototype, prototypeBand, overlay };
}
